import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { thumbnailOverflowWrapper } from '@pkg/components/Thumbnail/tokens';
import {
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledLayout,
    styledReset,
    styledTypography,
} from '@pkg/primitives';

import { tokens } from '../../tokens';

interface StyledCreditsProps {
    $compact?: boolean;
    $soloTitle?: boolean;
}

export const StyledCredits = styled.div<StyledCreditsProps>`
    --yat_nft-card-credits-thumbnail-size: ${({ $compact }) =>
        tokens.credits.thumbnail.size[$compact ? 'compact' : 'default']}px;
    --yat_nft-card-credits-subtitle-font-size: ${({ $compact }) =>
        tokens.credits.subtitle.fontSize[$compact ? 'compact' : 'default']}px;

    ${({ $compact, $soloTitle }) => {
        const fontSizeObj = tokens.credits.title.fontSize;

        if ($soloTitle) {
            return css`
                --yat_nft-card-credits-title-font-size: ${$compact
                    ? `${fontSizeObj.solo.compact}px`
                    : `${fontSizeObj.solo.default}px`};
            `;
        } else {
            return css`
                --yat_nft-card-credits-title-font-size: ${$compact
                    ? `${fontSizeObj.combo.compact}px`
                    : `${fontSizeObj.combo.default}px`};
            `;
        }
    }};
`;

export const LinkedContent = styled(Link)`
    ${styledReset.link};
    position: relative;
    z-index: 1;
    display: block;

    &::before {
        content: '';
        ${styledLayout.positionCover()};
        ${styledInteraction.interactionDisable};
        display: block;
        border-radius: ${tokens.credits.thumbnail.radius}px;
        opacity: 0;
        background-color: rgba(255, 255, 255, 0.1);
        transition: opacity ${duration.normal}ms ${easing.cubic};
    }

    ${styledInteraction.focusRing({
        offset: -4,
        radius: tokens.credits.thumbnail.radius,
        pseudoAfter: true,
    })};

    &:hover,
    &:focus {
        &::before {
            opacity: 1;
        }
    }

    &:active {
        &::before {
            opacity: 0.6;
        }
    }
`;

export const Content = styled.div`
    position: relative;
    z-index: 2;
    display: grid;
    align-items: center;
    grid-template-columns: auto 1fr auto;
    padding: ${tokens.card.padding}px;
`;

export const ThumbnailWrapper = styled.div`
    ${thumbnailOverflowWrapper(tokens.credits.thumbnail.radius)};
    width: var(--yat_nft-card-credits-thumbnail-size);
    height: var(--yat_nft-card-credits-thumbnail-size);
`;

export const Details = styled.div`
    display: grid;
    gap: ${tokens.credits.details.gap}px;

    &:nth-child(n + 2) {
        // Not using 'gap' on parent to avoid trailing gap
        // when 'children' is not present.
        margin-left: ${tokens.credits.wrapper.gap}px;
    }
`;

export const Title = styled.p`
    ${styledTypography.allianceBase};
    ${styledTypography.truncate};
    ${styledTypography.truncateHeightFix(true)};
    font-size: var(--yat_nft-card-credits-title-font-size);
    font-weight: 700;
`;

export const Subtitle = styled.p`
    ${styledTypography.allianceBase};
    ${styledTypography.truncate};
    ${styledTypography.truncateHeightFix(true)};
    font-size: var(--yat_nft-card-credits-subtitle-font-size);
    font-weight: 600;
    color: rgba(255, 255, 255, 0.5);
`;
